import { useCallback, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '../store';
import {
  setCurrentStep,
  addSession,
  addFork,
  brandId,
  type PromptSession,
  type TimelineStep,
  type TimelineFork,
  type PromptSessionId,
  type TimelineForkId,
  PromptSessionStatus,
} from '@repancy/core';

export interface UsePromptTimeTravelReturn {
  steps: TimelineStep[];
  currentStepIndex: number;
  currentStep: TimelineStep | undefined;
  highWaterMark: number;
  goToStep: (index: number) => void;
  goBack: () => void;
  goForward: () => void;
  forkFromHere: (label: string) => void;
  canGoBack: boolean;
  canGoForward: boolean;
}

export function usePromptTimeTravel(sessionId: string): UsePromptTimeTravelReturn {
  const dispatch = useAppDispatch();

  const stepIds = useAppSelector((s) => s.timelines.steps.ids);
  const stepEntities = useAppSelector((s) => s.timelines.steps.entities);
  const currentStepIndex = useAppSelector((s) => s.timelines.currentStepIndex);
  const highWaterMark = useAppSelector((s) => s.timelines.highWaterMark);
  const sessionEntity = useAppSelector((s) => s.promptSessions.sessions.entities[sessionId]);

  const steps = useMemo(
    () =>
      stepIds
        .map((id) => stepEntities[id])
        .filter((s): s is TimelineStep => s !== undefined && s.sessionId === sessionId),
    [stepIds, stepEntities, sessionId],
  );

  const currentStep = useMemo(
    () => (currentStepIndex >= 0 && currentStepIndex < steps.length ? steps[currentStepIndex] : undefined),
    [steps, currentStepIndex],
  );

  const canGoBack = currentStepIndex > 0;
  const canGoForward = currentStepIndex < steps.length - 1;

  const goToStep = useCallback(
    (index: number) => {
      dispatch(setCurrentStep(index));
    },
    [dispatch],
  );

  const goBack = useCallback(() => {
    if (canGoBack) {
      dispatch(setCurrentStep(currentStepIndex - 1));
    }
  }, [dispatch, currentStepIndex, canGoBack]);

  const goForward = useCallback(() => {
    if (canGoForward) {
      dispatch(setCurrentStep(currentStepIndex + 1));
    }
  }, [dispatch, currentStepIndex, canGoForward]);

  const forkFromHere = useCallback(
    (label: string) => {
      if (!sessionEntity) return;

      const forkedId = brandId<'PromptSession'>(crypto.randomUUID()) as PromptSessionId;
      const forkedSession: PromptSession = {
        ...sessionEntity,
        id: forkedId,
        status: PromptSessionStatus.Idle,
        createdAt: new Date().toISOString(),
        skillPrompts: sessionEntity.skillPrompts.map((sp) => ({ ...sp })),
        documentContextIds: [...sessionEntity.documentContextIds],
      };

      dispatch(addSession(forkedSession));

      // Without an addFork dispatch the ForkControls list stays empty
      // and the half-wired "Fork Here" button looks broken. Emit a
      // user-prompt delta carrying the parent's prompt as the
      // baseline — the user can edit the fork's prompt afterwards and
      // the diff view will show the divergence.
      const forkId = brandId<'TimelineFork'>(
        `fork-${forkedId}`,
      ) as TimelineForkId;
      const fork: TimelineFork = {
        id: forkId,
        forkStepIndex: currentStepIndex >= 0 ? currentStepIndex : 0,
        parentSessionId: sessionEntity.id,
        forkedSessionId: forkedId,
        label,
        deltas: [
          {
            kind: 'user-prompt',
            field: 'userPrompt',
            oldValue: sessionEntity.userPrompt,
            newValue: sessionEntity.userPrompt,
            timestamp: Date.now(),
          },
        ],
      };
      dispatch(addFork(fork));
    },
    [dispatch, sessionEntity, currentStepIndex],
  );

  return {
    steps,
    currentStepIndex,
    currentStep,
    highWaterMark,
    goToStep,
    goBack,
    goForward,
    forkFromHere,
    canGoBack,
    canGoForward,
  };
}
